/**
 * LibraryArtifactActions — 내 자료 상세의 받기 / 이어 수정 버튼 줄.
 *
 * 발표 모드 등 다른 에이전트가 넣을 버튼은 extra 슬롯으로만 붙인다.
 * 링크 공유·나눔터 공유는 부모가 그대로 담당한다.
 */

import React, { useCallback, useMemo, useRef, useState } from 'react';
import { fetchChatArtifact } from '../../api/chatApi';
import {
  remakeTextPptFile,
  remakeTextWorksheetExport,
  remakeTextWorksheetPdf,
} from '../../api/libraryArtifactFiles';
import { exportFreeDesign } from '../../api/freeDesignApi';
import { useChat } from '../../contexts/ChatContext';
import { auth } from '../../firebase/config';
import useFileDownload, { FILE_DOWNLOAD_LABELS } from '../../hooks/useFileDownload';
import { isWorksheetExportEnabled } from '../../utils/artifactHistory';
import { saveMyContent } from '../../api/galleryApi';
import {
  buildLibraryDownloadOptions,
  canResumeLibraryItem,
  pickResumeArtifactId,
  shouldOpenDownloadMenu,
} from '../../utils/libraryDownloadOptions';
import { filenameForFormat, pickLibraryRemakeOutline } from '../../utils/libraryFamily';
import { trackPptEvent, trackWorksheetEvent } from '../../utils/eventTracker';
import { loadOwnedConversation } from '../../utils/loadOwnedConversation';
import Dialog, { useSheetLayout } from '../common/Dialog';
import ModalActions from '../common/ModalActions';
import { Spinner } from '../common/AsyncState';
import LibraryDownloadMenu from './LibraryDownloadMenu';
import '../common/ModalActions.css';
import './LibraryArtifactActions.css';

const joinClassNames = (...values) => values.filter(Boolean).join(' ');

/** 옵션에 파일명이 없을 때의 저장 이름. 확장자는 옵션 형식을 따른다(학습지라고 .pdf 로 고정하지 않는다). */
const fallbackFilename = (item, format) => {
  const fmt = String(format || '').toLowerCase();
  if (item?.filename) return fmt ? filenameForFormat(item.filename, fmt) : item.filename;
  const title = String(item?.title || 'whatquiz').replace(/[/\\:*?"<>|]/g, '_').slice(0, 50);
  if (fmt) return `${title}.${fmt}`;
  if (item?.content_type === 'ppt') return `${title}.pptx`;
  if (item?.content_type === 'worksheet') return `${title}.pdf`;
  if (item?.content_type === 'image') return `${title}.png`;
  return title || 'whatquiz-file';
};

const outlineForRemake = (item, conversation) => pickLibraryRemakeOutline(item, conversation);

const trackLibraryDownload = (item, option) => {
  const track = item?.content_type === 'ppt' ? trackPptEvent
    : item?.content_type === 'worksheet' ? trackWorksheetEvent
      : null;
  if (!track) return;
  const format = option?.remake?.type === 'ppt-text' ? 'pptx' : String(option?.format || '').toLowerCase();
  track('artifact_downloaded', {
    conversationId: item.conversation_id || null,
    format,
    sourceMode: item.source_mode,
    location: 'library',
  });
};

const persistTextFile = async (item, result, outline) => {
  if (!item?.content_type || !result?.url) return;
  if (!item.conversation_id && !item.id) return;
  try {
    await saveMyContent({
      content_type: item.content_type,
      title: item.title || '',
      conversation_id: item.conversation_id || '',
      download_url: result.url,
      filename: result.filename,
      outline: outline || item.outline || null,
      source_mode: 'text',
      files_only: true,
      id: item.id || undefined,
    });
  } catch (_error) {
    // 받기는 됐고 문서 기록만 실패하면 다음에 다시 만들면 된다
  }
};

export default function LibraryArtifactActions({
  item,
  canDownload = false,
  extra = null,
  onContinueInChat,
  testId = 'gallery-my-detail-actions',
}) {
  const isStacked = useSheetLayout(true);
  const chat = useChat() || {};
  const conversations = useMemo(
    () => (Array.isArray(chat.conversations) ? chat.conversations : []),
    [chat.conversations],
  );
  const { download, isDownloading, buttonLabel } = useFileDownload();
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuOptions, setMenuOptions] = useState([]);
  const [menuHint, setMenuHint] = useState(null);
  const [hint, setHint] = useState(null);
  const [resolving, setResolving] = useState(false);
  const [continueBusy, setContinueBusy] = useState(false);
  const [missingOpen, setMissingOpen] = useState(false);
  const conversationRef = useRef(null);
  const cacheRef = useRef(new Map());

  const resolveConversation = useCallback(async () => {
    const conversationId = item?.conversation_id;
    if (!conversationId) return null;
    if (cacheRef.current.has(conversationId)) return cacheRef.current.get(conversationId);
    const fromList = conversations.find((conversation) => conversation.id === conversationId);
    if (fromList) {
      cacheRef.current.set(conversationId, fromList);
      return fromList;
    }
    const loaded = await loadOwnedConversation(conversationId, auth.currentUser?.uid);
    cacheRef.current.set(conversationId, loaded);
    return loaded;
  }, [conversations, item?.conversation_id]);

  const fulfillOption = useCallback(async (option, conversation) => {
    trackLibraryDownload(item, option);
    if (option.kind === 'direct' && option.url) {
      await download({ url: option.url, filename: option.filename || fallbackFilename(item, option.format) });
      return;
    }
    await download({
      filename: option.filename || fallbackFilename(item, option.format),
      getFile: async ({ signal }) => {
        if (option.remake?.type === 'chat-artifact') {
          const result = await fetchChatArtifact({
            file_id: option.remake.fileId || item.artifact_id,
            filename: option.filename || item.filename,
          });
          return { blob: result.blob, filename: result.filename || option.filename };
        }
        if (option.remake?.type === 'ppt-free-design') {
          return exportFreeDesign(option.remake.deckId, option.remake.format, { signal });
        }
        const outline = outlineForRemake(item, conversation);
        if (option.remake?.type === 'ppt-text') {
          const result = await remakeTextPptFile({
            outline,
            filename: option.filename,
            conversationId: item.conversation_id || null,
            preferCanvas: Boolean(option.remake.preferCanvas),
            signal,
          });
          await persistTextFile(item, result, outline);
          return result;
        }
        if (option.remake?.type === 'ws-export') {
          const result = await remakeTextWorksheetExport({
            outline,
            format: option.remake.format,
            filename: option.filename,
            conversationId: item.conversation_id || null,
            signal,
          });
          await persistTextFile(item, result, outline);
          return result;
        }
        if (option.remake?.type === 'ws-canvas-pdf') {
          const result = await remakeTextWorksheetPdf({
            outline,
            filename: option.filename,
            signal,
          });
          await persistTextFile(item, result, outline);
          return result;
        }
        if (option.url) return { url: option.url, filename: option.filename };
        throw new Error('받을 수 있는 파일이 없어요');
      },
    });
  }, [download, item]);

  const handleContinue = async () => {
    if (isDownloading || resolving || continueBusy) return;
    setContinueBusy(true);
    try {
      const conversation = await resolveConversation();
      conversationRef.current = conversation;
      if (canResumeLibraryItem(item, conversation) && onContinueInChat) {
        onContinueInChat(item, {
          artifactId: pickResumeArtifactId(item, conversation),
          conversation,
        });
        return;
      }
      setMissingOpen(true);
    } catch (_error) {
      setMissingOpen(true);
    } finally {
      setContinueBusy(false);
    }
  };

  const handleDownload = async () => {
    if (!canDownload || isDownloading || resolving || continueBusy) return;
    setResolving(true);
    try {
      const conversation = await resolveConversation();
      conversationRef.current = conversation;
      const { options, textHint } = buildLibraryDownloadOptions({
        item,
        conversation,
        exportEnabled: isWorksheetExportEnabled(),
      });
      setHint(textHint);
      if (options.length === 0) {
        await download({ filename: fallbackFilename(item) });
        return;
      }
      if (!shouldOpenDownloadMenu(options)) {
        await fulfillOption(options[0], conversation);
        return;
      }
      setMenuOptions(options);
      setMenuHint(textHint);
      setMenuOpen(true);
    } catch (error) {
      await download({
        filename: fallbackFilename(item),
        getFile: async () => { throw error; },
      });
    } finally {
      setResolving(false);
    }
  };

  const busy = isDownloading || resolving || continueBusy;
  const downloadAction = canDownload
    ? {
      label: (isDownloading || resolving) ? (buttonLabel || FILE_DOWNLOAD_LABELS.preparing) : '다운로드',
      onClick: handleDownload,
      loading: isDownloading || resolving,
    }
    : null;
  const continueAction = {
    label: continueBusy ? '대화를 찾는 중…' : '대화에서 이어서 수정하기',
    onClick: handleContinue,
    loading: continueBusy,
  };

  if (!continueAction && !downloadAction) return null;

  const primary = continueAction || downloadAction;
  const secondary = continueAction ? downloadAction : null;

  const renderButton = (action, kind) => {
    if (!action) return null;
    const loading = Boolean(action.loading);
    return (
      <button
        aria-busy={loading || undefined}
        className={joinClassNames(
          'whatquiz-modal-actions__btn',
          `whatquiz-modal-actions__btn--${kind}`,
        )}
        data-testid={`${testId}-${kind}`}
        disabled={busy}
        key={kind}
        onClick={action.onClick}
        type="button"
      >
        {loading && (
          <Spinner
            className="whatquiz-modal-actions__spinner"
            size={16}
            thickness={2}
            tone={kind === 'primary' ? 'inverse' : undefined}
          />
        )}
        <span>{action.label}</span>
      </button>
    );
  };

  const ordered = isStacked
    ? [renderButton(primary, 'primary'), renderButton(secondary, 'secondary')]
    : [renderButton(secondary, 'secondary'), renderButton(primary, 'primary')];

  return (
    <div className="library-artifact-actions gallery-detail-actions">
      <div
        className={joinClassNames(
          'whatquiz-modal-actions',
          isStacked ? 'whatquiz-modal-actions--stacked' : 'whatquiz-modal-actions--row',
        )}
        data-testid={testId}
      >
        {ordered}
      </div>
      {hint ? <p className="library-artifact-actions__hint">{hint}</p> : null}
      {extra ? <div className="library-artifact-actions__extra">{extra}</div> : null}
      <LibraryDownloadMenu
        busy={busy}
        onClose={() => setMenuOpen(false)}
        onSelect={async (option) => {
          setMenuOpen(false);
          await fulfillOption(option, conversationRef.current);
        }}
        open={menuOpen}
        options={menuOptions}
        textHint={menuHint}
      />
      <Dialog
        description="이 자료를 만든 대화가 없어서 이어서 수정할 수 없어요. 파일은 받을 수 있어요."
        footer={(
          <ModalActions
            primary={canDownload
              ? {
                label: (isDownloading || resolving)
                  ? (buttonLabel || FILE_DOWNLOAD_LABELS.preparing)
                  : '다운로드',
                loading: isDownloading || resolving,
                onClick: handleDownload,
              }
              : { label: '닫기', onClick: () => setMissingOpen(false) }}
            secondary={canDownload
              ? { label: '닫기', onClick: () => setMissingOpen(false) }
              : null}
            testId="library-resume-missing-actions"
          />
        )}
        onClose={() => setMissingOpen(false)}
        open={missingOpen}
        size="sm"
        testId="library-resume-missing"
        title="이어서 수정할 수 없어요"
      />
    </div>
  );
}
